import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { formatDate, formatMoney } from '@/lib/format';
import { useCampaignSpend, useRecordSpend, type Campaign } from './useCampaigns';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Manual spend entry (open question #3's default).
 *
 * Spend is recorded per day rather than as one running total, because #17 has to
 * be answerable for a period: "what did we spend on this campaign in October" has
 * no answer if there is only a single number. Re-entering a day corrects it —
 * the upsert is what stops a double entry quietly halving every ROAS figure.
 */
export function RecordSpendDialog({
  open,
  onClose,
  campaign,
}: {
  open: boolean;
  onClose: () => void;
  campaign: Campaign | undefined;
}) {
  const { t } = useTranslation();
  const record = useRecordSpend();
  const { data: history, isPending } = useCampaignSpend(open ? campaign?.id : undefined);

  const [spendDate, setSpendDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSpendDate(today());
    setAmount('');
    setNote('');
    setError(null);
    setSaved(false);
  }, [open]);

  const existing = (history ?? []).find((row) => row.spend_date === spendDate);

  const submit = async () => {
    if (!campaign) return;
    const value = Number(amount);
    if (!amount.trim() || Number.isNaN(value) || value < 0) {
      setError(t('campaigns.amountInvalid'));
      return;
    }
    try {
      await record.mutateAsync({
        campaignId: campaign.id,
        spendDate,
        amount: value,
        note: note.trim() || undefined,
      });
      setSaved(true);
      setAmount('');
      setNote('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('campaigns.recordSpend')} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {saved ? <Alert tone="success">{t('campaigns.spendSaved')}</Alert> : null}

        <p className="text-sm text-slate-600">
          {campaign?.name} · {t('campaigns.totalSpend')} {formatMoney(campaign?.total_spend)}
        </p>

        <Input
          label={t('campaigns.spendDate')}
          type="date"
          dir="ltr"
          value={spendDate}
          onChange={(e) => {
            setSpendDate(e.target.value);
            setSaved(false);
          }}
        />

        {/* Saying so before they type is kinder than surprising them after. */}
        {existing ? (
          <Alert tone="info">
            {t('campaigns.dayAlreadyRecorded', { amount: formatMoney(existing.amount) })}
          </Alert>
        ) : null}

        <Input
          label={t('campaigns.amount')}
          type="number"
          min="0"
          step="0.01"
          dir="ltr"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <Input label={t('campaigns.note')} value={note} onChange={(e) => setNote(e.target.value)} />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button loading={record.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>

        <div className="mt-2 border-t border-slate-200 pt-3">
          <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            {t('campaigns.recentSpend')}
          </h3>
          {isPending ? (
            <Spinner />
          ) : (history ?? []).length === 0 ? (
            <p className="text-xs text-slate-500">{t('campaigns.noSpendYet')}</p>
          ) : (
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {(history ?? []).slice(0, 20).map((row) => (
                <li key={row.id} className="flex justify-between gap-2 text-slate-600">
                  <span>{formatDate(row.spend_date)}</span>
                  <span dir="ltr">{formatMoney(row.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
